<template>
  <BmFooter
    id="footer"
    :links="footerLinks"
    :variant="
      $rttI18n.isGlsItSelected ||
      $rttI18n.isGlsPlSelected ||
      themeStore.themeId === 'gls-one'
        ? 'blue'
        : 'dark'
    "
  >
    <template v-if="!config.disablePoweredBy" #logo>
      <span class="visually-hidden">
        powered by {{ $rttI18n.isGlsItSelected ? 'Bettermile' : 'GLS' }}
      </span>
      <BettermileLogo
        v-if="$rttI18n.isGlsItSelected"
        aria-hidden="true"
        class="logo bettermile"
      />
      <GlsLogo v-else aria-hidden="true" class="logo gls" />
    </template>
  </BmFooter>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useTrackingDataStore } from '@/stores/trackingData.js';
import { useThemeStore } from '@/stores/theme.js';
import { configGetter } from '@/config/configGetter.js';
import BmFooter from '@/v2/design-system/components/Footer/BmFooter.vue';
import GlsLogo from '@/v2/design-system/assets/logos/GlsLogo.vue';
import BettermileLogo from '@/v2/design-system/assets/logos/BettermileLogo.vue';
import publicPathFunc from '@/util/publicPath.js';
import { sendAnalytics } from '@/services/analytics.js';

const $rttI18n = inject('rttI18n');
const $t = inject('t');
const $route = useRoute();

const trackingDataStore = useTrackingDataStore();
const themeStore = useThemeStore();

const publicPath = ref(publicPathFunc);

const externalContactLink = computed(() => {
  return $rttI18n.tenantSpecificContent('footer.contactLink');
});

const privacyContentExist = computed(() => {
  return $rttI18n.tenantSpecificContentExists('privacy.content');
});

const accessibilityContentExist = computed(() => {
  return $rttI18n.tenantSpecificContentExists('accessibility.content');
});

const contactLink = computed(() => {
  const routeName = $t(`languageSpecificRoutes.contact`);
  return `/${$rttI18n.tenantId ?? 'DE'}/${$rttI18n.locale}/${routeName}`;
});

const imprintLink = computed(() => {
  const routeName = $t(`languageSpecificRoutes.imprint`);
  if ($route.params.countryOrTenantId) {
    return `/${$route.params.countryOrTenantId}/${$rttI18n.locale}/${routeName}`;
  }
  return `/gls-de/${$rttI18n.locale}/imprint`;
});

const privacyLink = computed(() => {
  return $rttI18n.tenantSpecificWithFallback(
    'privacy.link',
    'glsGroup.privacy.link'
  );
});

const internalPrivacyLink = computed(() => {
  const routeName = $t(`languageSpecificRoutes.privacy`);
  if ($route.params.countryOrTenantId) {
    return `/${$route.params.countryOrTenantId}/${$rttI18n.locale}/${routeName}`;
  }
  return `/gls-de/${$rttI18n.locale}/privacy`;
});

const internalAccessibilityLink = computed(() => {
  const routeName = $t(`languageSpecificRoutes.accessibility`);
  if ($route.params.countryOrTenantId) {
    return `/${$route.params.countryOrTenantId}/${$rttI18n.locale}/${routeName}`;
  }
  return `/gls-de/${$rttI18n.locale}/accessibility`;
});

const config = computed(() => {
  return configGetter($rttI18n.tenantId, themeStore.themeId);
});

const sendClickStatsAnalytics = name => {
  sendAnalytics(trackingDataStore.data, 'clicksStats', name, { isV2: true });
};

// IMPORTANT: build the links in script and unwrap computed refs with `.value`
const footerLinks = computed(() =>
  [
    !config.value.disableImprintLink && {
      external: false,
      to: imprintLink.value,
      label: $t('imprint.name'),
      onClick: () => sendClickStatsAnalytics('imprintLink')
    },
    accessibilityContentExist.value
      ? {
          external: false,
          to: internalAccessibilityLink.value,
          label: $t('accessibility.label'),
          onClick: () => sendClickStatsAnalytics('internalAccessibilityLink')
        }
      : config.value.hasAccessibilityReport && {
          external: true,
          to: publicPath.value(
            `/accessibility-reports/${$rttI18n.tenantId}_accessibility_report.pdf`
          ),
          label: $t('accessibility.label'),
          onClick: () => sendClickStatsAnalytics('accessibilityReport')
        },
    privacyContentExist.value
      ? {
          external: false,
          to: internalPrivacyLink.value,
          label: $t('privacy.name'),
          onClick: () => sendClickStatsAnalytics('internalPrivacyLink')
        }
      : {
          external: true,
          to: privacyLink.value,
          label: $t('privacy.name'),
          onClick: () => sendClickStatsAnalytics('privacyLink')
        },
    externalContactLink.value
      ? {
          external: true,
          to: externalContactLink.value,
          label: $t('generic.contact'),
          onClick: () => sendClickStatsAnalytics('externalContactLink')
        }
      : !config.value.disableContactLink && {
          external: false,
          to: contactLink.value,
          label: $rttI18n.tenantSpecificWithFallback(
            'contact.label',
            'generic.contact'
          ),
          onClick: () => sendClickStatsAnalytics('contactLink')
        }
  ].filter(Boolean)
);
</script>

<style scoped>
.logo {
  height: 18px;
  width: auto;

  &.bettermile,
  &.gls {
    color: var(--neutral-text-icon-negative);
  }
}
</style>
